import { Button } from "@components/common/button/button";
import { Check } from "@components/common/icons/check";
import { Trash } from "@components/common/icons/trash";
import { GQLNetworkConnectorUserSocialAccountFragment } from "@components/network-connector/network-connector.gql.generated";
import { resolveChannelIcon, resolveChannelName, resolveChannelShortDescription } from "@utils/channel-resolver";
import { GQLChannel } from "graphql.generated";
import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

interface ChannelProps {
    channel: GQLChannel;
    isLoading: boolean;
    onConnect: (channel: GQLChannel) => void;
    onDisconnect: (channel: GQLChannel) => void;
    socialAccounts: GQLNetworkConnectorUserSocialAccountFragment[];
}

export const Channel: FunctionComponent<ChannelProps> = ({ channel, socialAccounts, isLoading, onConnect, onDisconnect }) => {
    const { t } = useTranslation();
    const isConnected = socialAccounts.find((item) => item.channel === channel && item.isConnected) ? true : false;

    return (
        <div className="relative flex aspect-square w-full items-center justify-center rounded-md bg-gray-100">
            <div className="grid w-full grid-cols-1 gap-y-3">
                <div className="mx-auto w-12">{resolveChannelIcon(channel, true, "size-full")}</div>
                <div className="text-center">
                    <div className="text-xs font-medium">{resolveChannelName(channel)}</div>
                    <div className="-mt-0.5 text-3xs text-gray-500">{resolveChannelShortDescription(channel, t)}</div>
                </div>
                <div className="text-center">
                    <Button
                        size="sm"
                        rounded="md"
                        label={
                            isConnected
                                ? t("socialConnector.connected", {
                                      defaultValue: "Connected",
                                  })
                                : t("socialConnector.connect", {
                                      defaultValue: "Connect",
                                  })
                        }
                        color={isConnected ? "primary" : undefined}
                        rightAdornments={
                            isConnected && (
                                <div className="transition-move duration-300 ease-out-cubic group-hover:rotate-[15deg]">
                                    <Trash />
                                </div>
                            )
                        }
                        loading={isLoading}
                        onClick={isConnected ? () => onDisconnect(channel) : () => onConnect(channel)}
                    />
                </div>
            </div>
            {isConnected && (
                <div className="pointer-events-none absolute bottom-0 h-10 w-full overflow-hidden rounded-md border-b-2 border-primary-500">
                    <div className="absolute bottom-0 right-0 flex size-6 items-center justify-center rounded-tl-md bg-primary-500 text-white">
                        <Check className="size-3" />
                    </div>
                </div>
            )}
        </div>
    );
};
